import { Form, useForm } from '@/components/forms';
import { Stepper } from '@/components/Stepper';
import { Button } from '@/design-system/button';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { FileObject, uploadFileToS3WithPresignedUrl } from '@/lib/uploadToAWS';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { BasicInfo } from './BasicInfo';
import { ClubInfo as ClubInfoForm } from './form/ClubInfo';

import { SNS } from '../Sns';
import { ClubAddress } from './form/ClubAddress';
import { Others } from './form/Others';
import useAddPremiumStoreInfo from './hooks/useAddPremiumStore';
import { formSchema } from './premiumStore.schema';
import { STORE_SETUP_STEPS } from './premiumStores.constant';
import { buildPremiumStorePayload } from './utils/buildPremiumStorePayload';

const DEFAULT_STEP = 1;

export const AddPremiumStores = () => {
  const [activeStep, setActiveStep] = useState(DEFAULT_STEP);

  const { mutateAsync, isSubmitting } = useAddPremiumStoreInfo();

  const form = useForm({
    schema: formSchema,
    mode: 'onBlur',
    defaultValues: {
      storeName: '',
      storeNameFurigana: '',
      groupName: '',
      description: '',
      businessType: '',
      ceoFullName: '',
      ceoFurigana: '',
      companyAddress: '',
      landlineNumber: '',
      mobileNumber: '',
      emailAddress: '',
      username: '',
      longitude: '',
      latitude: '',
      postalCode: '',
      prefecture: '',
      city: '',
      street: '',
      buildingNo: '',
      identificationType: '',
      kycFront: '',
      kycBack: '',
      businessLicenseNumber: '',
      licenseIssueDate: '',
      workingTimeFrom: '',
      workingTimeTo: '',
      lastEntryTime: '',
      holidays: '',
      tax: '',
      closingDate: '',
      instagram: '',
      tiktok: '',
      line: '',
      twitter: '',
      website: '',
      googleMap: '',
    },
  });
  const { t } = useDynamicLocale();
  const handleNext = async () => {
    const formData = form.getValues();
    const stepConfig = STORE_SETUP_STEPS.find((s) => s.step === activeStep);

    const isValid = await form.trigger(stepConfig?.fields, { shouldFocus: true });

    const profileImageFile = formData.profileImage?.valueOf();
    const coverImageFile = formData.coverImage?.valueOf();

    if (!profileImageFile) {
      toast.error(t('common.form.validation.profileUpload'));
      return;
    }
    if (!coverImageFile) {
      toast.error(t('common.form.validation.coverUpload'));
      return;
    }

    if (!isValid) return;

    setActiveStep((prev) => prev + 1);
  };
  return (
    <div className="rounded-lg bg-card shadow-panel p-8">
      <Stepper
        items={STORE_SETUP_STEPS}
        value={activeStep}
        defaultActive={DEFAULT_STEP}
        onValueChange={setActiveStep}
      />
      <Form
        form={form}
        onSubmit={async () => {
          const formData = form.getValues();

          const profileImageFile = formData.profileImage?.valueOf();
          const coverImageFile = formData.coverImage?.valueOf();
          const passportImageFile = formData.passportImage?.valueOf();
          const insuranceCardImageFile = formData.insuranceCardImage?.valueOf();
          const businessCertificateImageFile = formData.businessCertificateImage?.valueOf();
          const corporateRegistryImageFile = formData.corporateRegistryImage?.valueOf();
          const kycFrontImageFile = formData.kycFront?.valueOf();
          const kycBackImageFile = formData.kycBack?.valueOf();

          const [
            profileImageUrl,
            coverImageUrl,
            passportImageUrl,
            kycFrontImageUrl,
            kycBackImageUrl,
            insuranceCardImageUrl,
            businessCertificateImageUrl,
            corporateRegistryImageUrl,
          ] = await Promise.all([
            profileImageFile ? uploadFileToS3WithPresignedUrl(profileImageFile as FileObject) : Promise.resolve(''),
            coverImageFile ? uploadFileToS3WithPresignedUrl(coverImageFile as FileObject) : Promise.resolve(''),
            passportImageFile ? uploadFileToS3WithPresignedUrl(passportImageFile as FileObject) : Promise.resolve(''),
            kycFrontImageFile ? uploadFileToS3WithPresignedUrl(kycFrontImageFile as FileObject) : Promise.resolve(''),
            kycBackImageFile ? uploadFileToS3WithPresignedUrl(kycBackImageFile as FileObject) : Promise.resolve(''),
            insuranceCardImageFile
              ? uploadFileToS3WithPresignedUrl(insuranceCardImageFile as FileObject)
              : Promise.resolve(''),
            businessCertificateImageFile
              ? uploadFileToS3WithPresignedUrl(businessCertificateImageFile as FileObject)
              : Promise.resolve(''),
            corporateRegistryImageFile
              ? uploadFileToS3WithPresignedUrl(corporateRegistryImageFile as FileObject)
              : Promise.resolve(''),
          ]);

          const payload = buildPremiumStorePayload({
            formData,
            profileImageUrl,
            coverImageUrl,
            passportImageUrl,
            insuranceCardImageUrl,
            businessCertificateImageUrl,
            corporateRegistryImageUrl,
            kycFrontImageUrl,
            kycBackImageUrl,
          });
          await mutateAsync(payload);
        }}
      >
        <div className={activeStep === 1 ? 'block' : 'hidden'}>
          <BasicInfo />
        </div>
        <div className={activeStep === 2 ? 'block' : 'hidden'}>
          <div className="w-full flex flex-col gap-6 mt-8">
            <ClubAddress />
            <ClubInfoForm />
            <SNS storeType="premium" storeOrHost="store" />
            <Others />
          </div>
        </div>
        <div className="flex w-full gap-4 text-sm mt-6">
          {activeStep === 2 ? (
            <div className="flex justify-between w-full">
              <Link to="/store-information/premium-stores">
                <Button variant="outline" type="button" className="w-[122px] bg-transparent" disabled={isSubmitting}>
                  <Translation id="common.page.button.cancel" />
                </Button>
              </Link>
              <div className="flex flex-row-reverse gap-4">
                <Button type="submit" className="w-[110px]" disabled={isSubmitting}>
                  <Translation id="common.page.button.submit" />
                </Button>
                <Button
                  variant="outline"
                  className="w-[122px] bg-transparent"
                  disabled={isSubmitting}
                  onClick={() => setActiveStep(activeStep - 1)}
                >
                  <Translation id="common.page.button.previous" />
                </Button>
              </div>
            </div>
          ) : (
            <Button type="button" className="w-[110px] ml-auto" onClick={() => void handleNext()}>
              <Translation id="common.page.button.next" />
            </Button>
          )}
        </div>
      </Form>
    </div>
  );
};
